import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["heroCarousel", "heroImage", "title", "counter", "loader", "thumbnail", "carousel"]
  static values = { galleryId: String }

  declare readonly heroCarouselTarget: HTMLElement
  declare readonly heroImageTargets: HTMLElement[]
  declare readonly titleTarget: HTMLElement
  declare readonly hasTitleTarget: boolean
  declare readonly counterTarget: HTMLElement
  declare readonly hasCounterTarget: boolean
  declare readonly thumbnailTargets: HTMLElement[]
  declare readonly carouselTarget: HTMLElement
  
  private images: Array<{ title: string, index: number }> = []
  private currentIndex: number = 0
  private isScrolling: boolean = false

  connect() {
    this.collectImages()
    this.updateUI()
    
    // Initial scroll to current index if needed
    if (this.currentIndex > 0) {
      this.scrollToCurrentHero(false)
    }
  }

  collectImages() {
    this.images = this.thumbnailTargets.map((item, index) => ({
      title: (item as HTMLElement).dataset.galleryTitle || "",
      index: index
    }))
  }

  select(event: MouseEvent) {
    event.preventDefault()
    const clickedElement = event.currentTarget as HTMLElement
    this.currentIndex = parseInt(clickedElement.dataset.galleryIndex || "0")
    this.scrollToCurrentHero()
    this.updateUI()
  }

  next() {
    this.currentIndex = (this.currentIndex + 1) % this.images.length
    this.scrollToCurrentHero()
    this.updateUI()
  }

  prev() {
    this.currentIndex = (this.currentIndex - 1 + this.images.length) % this.images.length
    this.scrollToCurrentHero()
    this.updateUI()
  }

  // Handle manual swipes on the hero carousel
  onHeroScroll() {
    if (this.isScrolling) return

    const scrollLeft = this.heroCarouselTarget.scrollLeft
    const width = this.heroCarouselTarget.clientWidth
    const newIndex = Math.round(scrollLeft / width)

    if (newIndex !== this.currentIndex && newIndex >= 0 && newIndex < this.images.length) {
      this.currentIndex = newIndex
      this.updateUI()
    }
  }

  private scrollToCurrentHero(smooth = true) {
    this.isScrolling = true
    const target = this.heroImageTargets[this.currentIndex]
    if (target) {
      target.scrollIntoView({ 
        behavior: smooth ? 'smooth' : 'auto', 
        block: 'nearest', 
        inline: 'center' 
      })
    }
    
    // Reset scrolling flag after animation
    setTimeout(() => {
      this.isScrolling = false
    }, 500)
  }

  updateUI() {
    const image = this.images[this.currentIndex]
    if (image) {
      if (this.hasTitleTarget) this.titleTarget.textContent = image.title
      if (this.hasCounterTarget) this.counterTarget.textContent = `${this.currentIndex + 1} / ${this.images.length}`
      
      // Update thumbnails state
      this.thumbnailTargets.forEach((thumb, idx) => {
        const img = thumb.querySelector('img')
        if (idx === this.currentIndex) {
          thumb.classList.add('ring-2', 'ring-primary', 'ring-offset-4', 'ring-offset-surface')
          if (img) img.classList.remove('grayscale', 'opacity-60')
          thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
        } else {
          thumb.classList.remove('ring-2', 'ring-primary', 'ring-offset-4', 'ring-offset-surface')
          if (img) img.classList.add('grayscale', 'opacity-60')
        }
      })
    }
  }

  scrollThumbNext() {
    this.carouselTarget.scrollBy({ left: this.carouselTarget.clientWidth * 0.8, behavior: 'smooth' })
  }

  scrollThumbPrev() {
    this.carouselTarget.scrollBy({ left: -this.carouselTarget.clientWidth * 0.8, behavior: 'smooth' })
  }
}
